import { closeEditor } from 'ka-table/actionCreators';
import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Overlay, Popover } from 'react-bootstrap';
import { BsCheckLg, BsXLg } from 'react-icons/bs';

import {
	regexIsValidPathTest,
	regexHasForbiddenCharTest,
	regexIsMediaTest,
} from '../../utilities/regex';

const regexIsValidPath = new RegExp(regexIsValidPathTest);
const regexHasForbiddenChar = new RegExp(regexHasForbiddenCharTest);
const regexIsMedia = new RegExp(regexIsMediaTest);

interface Column {
	key: string;
}

interface RowData {
	_type?: string;
}

interface CellEditorProps {
	column: Column;
	rowKeyValue: any;
	dispatch: (action: any) => void;
	rowData: RowData;
	value: any;
	minWidth?: number;
	onSave: (rowKeyValue: any, columnKey: string, value: any, rowData: RowData) => void;
	t: (key: string) => string;
}

const CellEditor: React.FC<CellEditorProps> = ({
	column,
	rowKeyValue,
	dispatch,
	rowData,
	value,
	minWidth = 0,
	onSave,
	t,
}) => {
	const [editorValue, setValue] = useState<string>(value);
	const [editorWidth, setEditorWidth] = useState<number>(18);
	const [inputError, setInputError] = useState<boolean>(false);
	const target = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (
			(editorValue?.length > minWidth || editorValue?.length > editorWidth) &&
			!minWidth
		) {
			setEditorWidth(editorValue.length);
		} else if (minWidth) {
			setEditorWidth(minWidth);
		}
	}, [editorValue, minWidth, editorWidth]);

	const close = () => {
		dispatch(closeEditor(rowKeyValue, column.key));
	};

	const handleInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		/**
		 * Must not be empty or blank.
		 * Must not be more than 64 characters.
		 * Must not contain forbidden chars or invalid spaces or dots
		 * Must have a valid image or video extension
		 */
		if (
			event.target.value.length > 0 &&
			event.target.value.length <= 64 &&
			regexIsValidPath.test(event.target.value) &&
			!regexHasForbiddenChar.test(event.target.value) &&
			(rowData?._type === 'Entry$File'
				? regexIsMedia.test(event.target.value)
				: true)
		) {
			setInputError(false);
		} else {
			setInputError(true);
		}

		setValue(event.target.value);
	};

	const handleSave = (updatedValue: string) => {
		setValue(updatedValue);
		onSave(rowKeyValue, column.key, updatedValue, rowData);
		close();
	};

	return (
		<div className="bay-table-custom-editor w-100 me-3">
			<Form.Group className="my-2">
				<div className="d-flex align-items-center mb-2">
					<Form.Control
						ref={target}
						required
						autoComplete="off"
						className={`lh-1 w-100 ${!inputError ? '' : 'is-invalid'}`}
						id="edit_entry-input_name"
						isValid={
							editorValue.length > 0 &&
							editorValue.length <= 64 &&
							regexIsValidPath.test(editorValue) &&
							!regexHasForbiddenChar.test(editorValue)
						}
						maxLength={64}
						placeholder={`${t('enter')} ${t(rowData?._type === 'Entry$File' ? 'fileName' : 'folderName')}`}
						type="text"
						value={editorValue}
						onChange={(e) => handleInputChanged(e)}
					/>
					<Button
						className={`${inputError ? 'bay-editable-error' : ''} bay-editable-button bay-editable-button-save text-light mx-2 fs-5 py-1 px-2`}
						disabled={
							editorValue === value ||
							editorValue.length === 0 ||
							!regexIsValidPath.test(editorValue) ||
							regexHasForbiddenChar.test(editorValue)
						}
						variant="success"
						onClick={() => handleSave(editorValue)}
					>
						<BsCheckLg />
					</Button>
					<Button
						className="bay-editable-button bay-editable-button-cancel fs-5 py-1 px-2"
						variant="secondary"
						onClick={() => close()}
					>
						<BsXLg />
					</Button>
				</div>
			</Form.Group>
			<Overlay
				flip
				placement="top-start"
				show={inputError}
				target={target.current}
			>
				{(props) => (
					<Popover {...props}>
						<div className="alert alert-danger m-2" role="alert">
							<h2>
								{t(
									rowData?._type === 'Entry$File'
										? 'invalidFileName'
										: 'invalidFolderName',
								)}
							</h2>
							<p>
								{t(
									rowData?._type === 'Entry$File'
										? 'invalidFileNameContent'
										: 'invalidFolderNameContent',
								)}
							</p>
							<ul>
								<li>Must have a minimum of 1 character.</li>
								<li>{'Must not contain + = | * ? / \\ : < > $'}</li>
								<li>Must not start or end with .</li>
								<li>Must not start or end with a space</li>
								<li>Must not contain double spaces</li>
								{rowData?._type === 'Entry$File' && (
									<li>
										Must have a valid image extension (ie: .jpg, .png) or video
										extension (ie: .mp4, .mov)
									</li>
								)}
							</ul>
						</div>
					</Popover>
				)}
			</Overlay>
		</div>
	);
};

export default CellEditor;
